import { normalizeSize, dominantCartSize } from './her-dad-cart-size-utils.js';
import { StandardEvents } from '@shopify/events';

// Keep repeat-variant detection consistent with the cart reminder, including
// a variant split over multiple lines by discounts or custom properties.
function reviewCart(cart) {
  if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
  const items = cart.items.filter((item) => Number(item.quantity) > 0);
  const sizes = new Map();
  const variants = new Map();
  for (const item of items) {
    const size = normalizeSize(item.options_with_values?.find((option) => option.name?.trim().toLowerCase() === 'size')?.value);
    const quantity = Number(item.quantity);
    if (size) sizes.set(size, (sizes.get(size) || 0) + quantity);
    const id = String(item.variant_id ?? item.id);
    const group = variants.get(id) || { ...item, quantity: 0 };
    group.quantity += quantity;
    variants.set(id, group);
  }
  const mixed = sizes.size > 1;
  const duplicates = [...variants.values()].filter((item) => item.quantity > 1);
  return {
    empty: items.length === 0,
    flagged: mixed || duplicates.length > 0,
    mixed,
    dominantSize: dominantCartSize(items),
    sizes,
    duplicates: duplicates.length > 0,
    items: mixed ? [...variants.values()] : duplicates,
    // Ignore line ordering, but distinguish a new cart and every variant,
    // quantity or line-property change (properties are included in line keys).
    signature: JSON.stringify([cart.token ?? '', items.map((item) => JSON.stringify([
      item.key, item.variant_id ?? item.id, Number(item.quantity), item.options_with_values,
    ])).sort()]),
  };
}

const dialog = document.getElementById('HdCheckoutReview');
if (dialog && typeof dialog.showModal === 'function') {
  const home = dialog.parentElement;
  const heading = dialog.querySelector('h2');
  const list = dialog.querySelector('[data-review-items]');
  const sizes = dialog.querySelector('[data-review-sizes]');
  const sizeCounts = dialog.querySelector('[data-review-size-counts]');
  const duplicates = dialog.querySelector('[data-review-duplicates]');
  const status = dialog.querySelector('[data-review-status]');
  const next = dialog.querySelector('[data-review-continue]');
  const pending = new Set();
  let quantityChangedAt = 0;
  let revision = 0;
  let intent = null;
  let allowedForm = null;
  const reviewKey = 'her-dad:cart-review:v1';
  const cartPath = new URL(dialog.dataset.cartUrl, window.location.origin).pathname.replace(/\.js$/, '');
  let reviewedSignature = null;

  function rememberReview(signature) {
    reviewedSignature = signature;
    try {
      if (signature) window.sessionStorage.setItem(reviewKey, signature);
      else window.sessionStorage.removeItem(reviewKey);
    } catch {}
  }

  function reviewedOnCartPage(signature) {
    try { reviewedSignature = window.sessionStorage.getItem(reviewKey); } catch {}
    if (reviewedSignature !== signature) {
      if (reviewedSignature) rememberReview(null);
      return false;
    }
    // The next checkout click on the edit destination confirms that review.
    // A hash alone never acknowledges a cart, and every click still reads it.
    return window.location.pathname === cartPath && window.location.hash === '#review-cart';
  }

  document.addEventListener('quantity-selector:update', (event) => {
    if (!event.target.closest?.('cart-items-component')) return;
    quantityChangedAt = Date.now();
    revision++;
  }, true);
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    revision++;
    if (!event.promise) return;
    const settled = Promise.resolve(event.promise).catch(() => {}).finally(() => pending.delete(settled));
    pending.add(settled);
  }, true);

  const currentButton = (state) => {
    const form = state?.form?.isConnected ? state.form : document.getElementById('cart-form');
    return form && [...form.elements].find((element) => element.name === 'checkout');
  };
  const restoreButton = (state) => {
    state.button.removeAttribute('aria-busy');
    if (state.label) state.label.textContent = state.labelText;
  };
  const showDialog = (state) => {
    if (dialog.open) return;
    // The desktop drawer has its own focus trap. Nest the modal within that
    // panel, outside the cart section that Shopify replaces after cart edits.
    const panel = currentButton(state)?.closest('dialog[open]');
    (panel || home).append(dialog);
    dialog.showModal();
    heading.focus();
  };
  const resetIntent = () => {
    const state = intent;
    intent = null;
    if (state) restoreButton(state);
    return state;
  };
  const dismiss = () => {
    const state = resetIntent();
    if (dialog.open) dialog.close();
    currentButton(state || {})?.focus?.({ preventScroll: true });
  };

  async function freshCart() {
    const controller = new AbortController();
    let timeout;
    try {
      return await Promise.race([
        (async () => {
          let before;
          let cart;
          do {
            // Match the theme's 300ms quantity debounce before waiting for its
            // quantity requests AND their section renders to complete.
            const delay = Math.max(0, quantityChangedAt + 350 - Date.now());
            if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
            let updates;
            do {
              updates = [...pending, ...(customElements.get('cart-items-component')?.pendingQuantityUpdates || [])];
              if (updates.length) await Promise.all(updates);
            } while (updates.length);
            before = revision;
            const response = await fetch(dialog.dataset.cartUrl, {
              method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
              headers: { Accept: 'application/json' },
            });
            if (!response.ok) throw new Error('Cart check failed');
            cart = await response.json();
          } while (before !== revision);
          return reviewCart(cart);
        })(),
        new Promise((_, reject) => {
          timeout = setTimeout(() => { controller.abort(); reject(new Error('Cart check timed out')); }, 12000);
        }),
      ]);
    } finally {
      clearTimeout(timeout);
    }
  }

  function renderReview(review, changed) {
    sizes.hidden = !review.mixed;
    sizeCounts.textContent = [...review.sizes].map(([size, count]) => `${count} × ${size}`).join(' · ');
    duplicates.hidden = !review.duplicates;
    list.replaceChildren();
    for (const item of review.items) {
      const row = document.createElement('li');
      row.className = 'hd-checkout-review__item';
      const info = document.createElement('span');
      const name = document.createElement('span');
      name.className = 'hd-checkout-review__name';
      name.textContent = item.product_title || item.title;
      const variant = document.createElement('span');
      variant.className = 'hd-checkout-review__variant';
      const options = (item.options_with_values || []).filter((option) => option.value !== 'Default Title');
      options.forEach((option, index) => {
        if (index) variant.append(document.createTextNode(' / '));
        const value = document.createElement('span');
        value.textContent = option.value;
        const isSize = option.name?.trim().toLowerCase() === 'size';
        if (isSize && review.mixed && (!review.dominantSize || normalizeSize(option.value) !== review.dominantSize)) {
          value.dataset.hdReviewHighlight = 'size';
          value.setAttribute('aria-describedby', 'HdCartReviewSizeHint');
        }
        variant.append(value);
      });
      const quantity = document.createElement('span');
      quantity.className = 'hd-checkout-review__quantity';
      quantity.textContent = `${dialog.dataset.quantity} ${item.quantity}`;
      if (item.quantity > 1) {
        quantity.dataset.hdReviewHighlight = 'quantity';
        quantity.setAttribute('aria-describedby', 'HdCartReviewQuantityHint');
      }
      info.append(name, variant);
      row.append(info, quantity);
      list.append(row);
    }
    status.textContent = changed ? dialog.dataset.changed : '';
    status.hidden = !changed;
    next.hidden = false;
    next.textContent = dialog.dataset.continue;
  }

  function proceed(state) {
    const button = currentButton(state);
    if (!button?.form || button.disabled) throw new Error('Checkout form changed');
    restoreButton(state);
    intent = null;
    if (dialog.open) dialog.close();
    // Quantity edits have already settled in Shopify. Do not let stale form
    // inputs (for example after a change in another tab) overwrite that cart.
    // Keep the original checkout submitter, note and native validation.
    const keepCheckedQuantities = (event) => event.formData.delete('updates[]');
    button.form.addEventListener('formdata', keepCheckedQuantities, { once: true });
    allowedForm = button.form;
    try { button.form.requestSubmit(button); }
    finally {
      allowedForm = null;
      button.form.removeEventListener('formdata', keepCheckedQuantities);
    }
  }

  async function check(state, confirmed = false) {
    if (state.checking) return;
    state.checking = true;
    next.disabled = true;
    next.textContent = dialog.dataset.checking;
    state.button.setAttribute('aria-busy', 'true');
    if (state.label) state.label.textContent = dialog.dataset.checking;
    try {
      const review = await freshCart();
      if (intent !== state) return;
      if (review.empty) {
        rememberReview(null);
        state.signature = null;
        sizes.hidden = duplicates.hidden = next.hidden = true;
        list.replaceChildren();
        status.textContent = dialog.dataset.empty;
        status.hidden = false;
        showDialog(state);
        return;
      }
      const alreadyReviewed = reviewedOnCartPage(review.signature);
      if (!review.flagged || alreadyReviewed || (confirmed && review.signature === state.signature)) {
        proceed(state);
        return;
      }
      renderReview(review, Boolean(state.signature && state.signature !== review.signature));
      state.signature = review.signature;
      showDialog(state);
    } catch (error) {
      if (intent !== state) return;
      state.signature = null;
      status.textContent = dialog.dataset.error;
      status.hidden = false;
      next.hidden = false;
      next.textContent = dialog.dataset.retry;
      showDialog(state);
    } finally {
      state.checking = false;
      next.disabled = false;
      restoreButton(state);
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
    const button = event.submitter || [...form.elements].find((element) => element.name === 'checkout');
    if (!button || button.name !== 'checkout') return;
    if (allowedForm === form) { allowedForm = null; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (intent) return;
    const label = button.querySelector('.button-text');
    intent = { form, button, label, labelText: label?.textContent, signature: null, checking: false };
    sizes.hidden = duplicates.hidden = status.hidden = true;
    list.replaceChildren();
    check(intent);
  }, true);
  next.addEventListener('click', () => { if (intent) check(intent, Boolean(intent.signature)); });
  dialog.querySelector('[data-review-close]').addEventListener('click', dismiss);
  dialog.querySelector('[data-review-edit]').addEventListener('click', () => {
    if (intent?.signature) rememberReview(intent.signature);
    resetIntent();
    dialog.close();
  });
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); dismiss(); });
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !event.isComposing) {
      event.preventDefault();
      event.stopPropagation();
      dismiss();
    }
  });
  dialog.addEventListener('close', () => {
    // Responsive drawer restacking closes and reopens a nested dialog in the
    // same task; preserve the review in that case.
    if (dialog.open) return;
    resetIntent();
    home.append(dialog);
  });
  window.addEventListener('pagehide', () => { resetIntent(); if (dialog.open) dialog.close(); });
}
